import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  Plus,
  Key,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from './ui/Button'
import { FieldLabel } from './ui/Field'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { api, extractApiError } from '../lib/api'
import { lengthError } from '../lib/fieldLength'
import { formatDateTime } from '../lib/format'

/**
 * Bring-your-own AI provider keys — laid out like the SOP Integration
 * page (form card on the left, side rail with last-test result + how-
 * this-works on the right). A tab switcher at the top picks which
 * provider you're configuring; the form below + side rail follow.
 *
 * Drives entirely from the backend catalogue
 * (`GET /api-keys/ai-providers/catalogue`) so adding a new provider
 * means appending one entry server-side; this component doesn't need
 * to change.
 */
export function AiProviderKeysSection() {
  const qc = useQueryClient()

  const catalogueQuery = useQuery({
    queryKey: ['ai-providers', 'catalogue'],
    queryFn: async () => {
      const res = await api.get('/api-keys/ai-providers/catalogue')
      return res.data
    },
    staleTime: Infinity,
  })
  const statusQuery = useQuery({
    queryKey: ['ai-providers', 'status'],
    queryFn: async () => {
      const res = await api.get('/api-keys/ai-providers')
      return res.data
    },
  })

  const catalogue = catalogueQuery.data?.data ?? []
  const status = statusQuery.data?.data ?? []
  const statusByCode = useMemo(
    () => new Map(status.map((s) => [s.code, s])),
    [status],
  )

  /** Currently-selected provider tab. Defaults to the first
   *  provider in the catalogue once it loads. */
  const [activeCode, setActiveCode] = useState(null)
  useEffect(() => {
    if (activeCode === null && catalogue.length > 0) {
      setActiveCode(catalogue[0].code)
    }
  }, [catalogue, activeCode])

  const active = catalogue.find((p) => p.code === activeCode)
  const activeStatus = activeCode ? statusByCode.get(activeCode) : null
  const configured = Boolean(activeStatus?.configured)
  // Which provider actually runs the free-text AI features (report rollups,
  // workspace AI template). SOP→tasks always uses Anthropic.
  const activeProviderCode = statusQuery.data?.activeProvider ?? 'anthropic'
  const isSelectedTheActiveProvider = activeCode === activeProviderCode

  // ── Form state, reset whenever the selected provider changes ────
  const [key, setKey] = useState('')
  const [keyDirty, setKeyDirty] = useState(false)
  const [model, setModel] = useState('')
  // testMutation.data carries the most recent test result so the side
  // rail can render success / failure detail without a separate query.

  useEffect(() => {
    if (!active) return
    setKey('')
    setKeyDirty(false)
    // Pre-fill only a previously-saved model. When nothing is saved,
    // leave the field blank so the placeholder shows — we no longer
    // seed the provider's default model into the input.
    setModel(activeStatus?.model ?? '')
  }, [active, activeStatus])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        key: key.trim(),
        model: model.trim() || undefined,
      }
      const res = await api.put(
        `/api-keys/ai-providers/${active.code}`,
        body,
      )
      return res.data
    },
    onSuccess: () => {
      toast.success(`${active.label} key saved & verified.`)
      void qc.invalidateQueries({ queryKey: ['ai-providers', 'status'] })
      setKey('')
      setKeyDirty(false)
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      toast.error(fields?.key ?? message)
    },
  })

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/api-keys/ai-providers/${active.code}/test`,
      )
      return res.data
    },
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(result.message ?? 'Key verified successfully.')
        void qc.invalidateQueries({ queryKey: ['ai-providers', 'status'] })
      } else {
        toast.error(result.message ?? 'Provider rejected the key.')
      }
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const removeMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/api-keys/ai-providers/${active.code}`)
    },
    onSuccess: () => {
      toast.success(`${active.label} key removed successfully.`)
      void qc.invalidateQueries({ queryKey: ['ai-providers', 'status'] })
      setConfirmRemove(false)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const setActiveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.put('/api-keys/ai-providers/active', {
        provider: active.code,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success(`${active.label} is now the active AI provider.`)
      void qc.invalidateQueries({ queryKey: ['ai-providers', 'status'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const [confirmRemove, setConfirmRemove] = useState(false)

  const isLoading = catalogueQuery.isLoading || statusQuery.isLoading
  const testResult = testMutation.data
  const lastTestOk = testResult?.ok === true
  const lastTestFailed = testResult?.ok === false

  return (
    <section className="mb-8">
      {/* Header — same shape as SOP Integration: icon + h1 + subtitle
       *  on the left, a status pill on the right. */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] shrink-0">
            <Key size={22} />
          </span>
          <div>
            <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] leading-tight">
              AI Provider Keys
            </h2>
            <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
              Plug in your own AI keys so features run on your subscription.
            </p>
          </div>
        </div>
        {!isLoading && active ? (
          <span
            className={
              'shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ' +
              (configured
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10'
                : 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]')
            }
          >
            <span
              aria-hidden="true"
              className={
                'h-1.5 w-1.5 rounded-full ' +
                (configured
                  ? 'bg-emerald-500'
                  : 'bg-[color:var(--color-ink-muted)]')
              }
            />
            {configured ? 'Connected' : 'Not configured'}
          </span>
        ) : null}
      </div>

      {isLoading || !active ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" />
          Loading providers…
        </p>
      ) : (
        <>
          {/* Provider tabs — pill switcher above the form. Mirrors the
           *  view-tab pattern used elsewhere in the app (Overview /
           *  List / Board on the Space page). */}
          <div className="mb-4 flex items-center gap-1 border-b border-[color:var(--color-rule)] -mt-2">
            {catalogue.map((p) => {
              const s = statusByCode.get(p.code)
              const isActive = p.code === activeCode
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => setActiveCode(p.code)}
                  aria-selected={isActive}
                  className={
                    'inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors cursor-pointer ' +
                    (isActive
                      ? 'border-[color:var(--color-accent-strong)] text-[color:var(--color-ink-strong)] font-semibold'
                      : 'border-transparent text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
                  }
                >
                  {p.label}
                  {s?.configured ? (
                    <span
                      aria-hidden="true"
                      title="Connected"
                      className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                    />
                  ) : null}
                  {p.code === activeProviderCode ? (
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-[color:var(--color-accent-strong)]">
                      Active
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form card — two-thirds of the row on wide screens. Same
             *  shape as SOP Integration's "Endpoint" card. */}
            <section className="lg:col-span-2 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
              <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-4">
                {active.label}
              </h3>
              <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
                {active.description}
              </p>

              <div className="space-y-4">
                <div>
                  <FieldLabel
                    label="API Key"
                    htmlFor="ai-provider-key"
                    required={!configured}
                  />
                  <input
                    id="ai-provider-key"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={
                      configured && !keyDirty
                        ? `${activeStatus?.masked ?? '••••'} already saved — leave blank to keep`
                        : 'Enter API Key'
                    }
                    value={key}
                    onChange={(e) => {
                      setKey(e.target.value)
                      setKeyDirty(true)
                    }}
                    maxLength={500}
                    className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
                  />
                  <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                    Encrypted at rest with AES-256-GCM. Verified against
                    the provider before saving.
                    {active.consoleUrl ? (
                      <>
                        {' '}
                        <a
                          href={active.consoleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
                        >
                          Get a key
                          <ExternalLink size={11} />
                        </a>
                      </>
                    ) : null}
                  </p>
                </div>

                <div>
                  <FieldLabel label="Model" htmlFor="ai-provider-model" />
                  <input
                    id="ai-provider-model"
                    type="text"
                    spellCheck={false}
                    placeholder="Enter the Model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className={
                      'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] ' +
                      (lengthError(model, undefined, 200, 'Model')
                        ? 'border-[color:var(--color-priority-urgent)]'
                        : 'border-[color:var(--color-rule)]')
                    }
                  />
                  {lengthError(model, undefined, 200, 'Model') ? (
                    <p role="alert" className="mt-1 text-sm text-[color:var(--color-priority-urgent)]">
                      {lengthError(model, undefined, 200, 'Model')}
                    </p>
                  ) : null}
                  <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                    Leave blank to use the provider&apos;s default model.
                    Enter a model name only if your account uses a
                    specific one.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-[color:var(--color-rule)] flex items-center gap-3 flex-wrap">
                <Button
                  onClick={() => saveMutation.mutate()}
                  loading={saveMutation.isPending}
                  disabled={
                    saveMutation.isPending ||
                    // Save requires a fresh key when nothing's saved.
                    // When already configured, a blank key + same model
                    // means "no change" — disable to avoid pointless
                    // round-trips.
                    (!keyDirty && !configured) ||
                    (!keyDirty &&
                      configured &&
                      (model.trim() === '' ||
                        model.trim() === (activeStatus?.model ?? '')))
                  }
                >
                  {configured ? 'Update' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  loading={testMutation.isPending}
                  disabled={!configured || testMutation.isPending}
                  title={
                    configured
                      ? `Verify the saved ${active.label} key`
                      : 'Save the key first.'
                  }
                >
                  Test Connection
                </Button>
                {configured ? (
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmRemove(true)}
                    disabled={removeMutation.isPending}
                    title={`Remove ${active.label} key`}
                  >
                    <Trash2 size={14} />
                    Remove
                  </Button>
                ) : null}
                {isSelectedTheActiveProvider ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]">
                    <CheckCircle2 size={13} />
                    Active provider
                  </span>
                ) : active.code === 'anthropic' || configured ? (
                  <Button
                    variant="secondary"
                    onClick={() => setActiveMutation.mutate()}
                    loading={setActiveMutation.isPending}
                    title={`Use ${active.label} for report + workspace AI`}
                  >
                    Make Active
                  </Button>
                ) : null}
                {activeStatus?.lastVerifiedAt ? (
                  <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
                    Last verified {formatDateTime(activeStatus.lastVerifiedAt)}
                  </span>
                ) : null}
              </div>
            </section>

            {/* Side rail — last-test result + how-this-works helper.
             *  Same two-card stack as SOP Integration's aside. */}
            <aside className="space-y-4">
              <section
                className={
                  'rounded-xl border p-4 ' +
                  (lastTestOk
                    ? 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-500/25 dark:bg-emerald-500/[0.06]'
                    : lastTestFailed
                      ? 'border-[color:var(--color-priority-urgent)]/40 bg-[color:var(--color-priority-urgent)]/5'
                      : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)]')
                }
              >
                <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
                  Last Test
                </h3>
                {testResult ? (
                  <>
                    <p
                      className={
                        'text-sm font-medium m-0 mb-2 ' +
                        (lastTestOk
                          ? 'text-emerald-700'
                          : 'text-[color:var(--color-priority-urgent)]')
                      }
                    >
                      {lastTestOk ? 'Success' : 'Failed'}
                    </p>
                    <p className="text-sm text-[color:var(--color-ink)] m-0 break-words">
                      {testResult.message}
                    </p>
                    {testResult.lastVerifiedAt ? (
                      <p className="mt-2 text-sm text-[color:var(--color-ink-muted)] tabular-nums m-0">
                        Verified at {formatDateTime(testResult.lastVerifiedAt)}
                      </p>
                    ) : null}
                  </>
                ) : configured ? (
                  <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
                    Click <strong>Test Connection</strong> to verify the
                    saved key against {active.label}.
                  </p>
                ) : (
                  <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
                    No key saved yet. Add one above, then come back to
                    test it.
                  </p>
                )}
              </section>

              <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
                <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-2">
                  How This Works
                </h3>
                <ol className="text-sm text-[color:var(--color-ink)] space-y-2 list-decimal pl-4 m-0">
                  <li>
                    Get an API key from{' '}
                    <a
                      href={active.consoleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[color:var(--color-accent-strong)] hover:underline"
                    >
                      the provider&apos;s console
                    </a>
                    .
                  </li>
                  <li>Paste it here and click Save — we verify it live.</li>
                  <li>
                    Set it as the <strong>active provider</strong> — report
                    rollups and workspace AI template generation then run on
                    it. (SOP→tasks always uses Anthropic.)
                  </li>
                  <li>
                    Remove the key any time — TaskMagix falls back to its
                    shared key (if any) or skips the provider.
                  </li>
                </ol>
                <p className="mt-3 text-sm text-[color:var(--color-ink-muted)] inline-flex items-start gap-1.5">
                  <CheckCircle2
                    size={14}
                    className="text-[color:var(--color-success)] mt-0.5 shrink-0"
                  />
                  <span>
                    Stored encrypted at rest. Never shown back in plaintext.
                  </span>
                </p>
              </section>
            </aside>
          </div>

          <FallbackOrderCard
            catalogue={catalogue}
            statusByCode={statusByCode}
            activeProviderCode={activeProviderCode}
          />
        </>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title={active ? `Remove ${active.label} key?` : 'Remove key?'}
        message="TaskMagix will fall back to its shared key (or stop running this provider if no shared key is configured). You can add a new one any time."
        confirmLabel="Remove"
        variant="danger"
        confirming={removeMutation.isPending}
        onCancel={() =>
          removeMutation.isPending ? undefined : setConfirmRemove(false)
        }
        onConfirm={() => removeMutation.mutate()}
      />
    </section>
  )
}

/**
 * Runtime fallback order. When the active provider fails with a temporary /
 * provider error (rate limit, timeout, 5xx…), the providers listed here are
 * tried next, in order. Only providers with a saved key participate — the
 * active provider is always tried first and doesn't need to appear here.
 */
function FallbackOrderCard({ catalogue, statusByCode, activeProviderCode }) {
  const qc = useQueryClient()
  const labelOf = (code) =>
    catalogue.find((p) => p.code === code)?.label ?? code

  const orderQuery = useQuery({
    queryKey: ['ai-providers', 'fallback-order'],
    queryFn: async () => {
      const res = await api.get('/api-keys/ai-providers/fallback-order')
      return res.data
    },
  })

  const [order, setOrder] = useState(null)
  useEffect(() => {
    if (order === null && orderQuery.data) {
      setOrder(orderQuery.data.fallbackOrder ?? [])
    }
  }, [orderQuery.data, order])

  const saveMutation = useMutation({
    mutationFn: async (next) => {
      const res = await api.put('/api-keys/ai-providers/fallback-order', {
        order: next,
      })
      return res.data
    },
    onSuccess: (data) => {
      toast.success('Fallback order saved.')
      qc.setQueryData?.(['ai-providers', 'fallback-order'], data)
      void qc.invalidateQueries({ queryKey: ['ai-providers', 'fallback-order'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (orderQuery.isLoading || order === null) return null

  // Configured providers (have a saved key) other than the active one are the
  // candidates for fallback. The active provider is always the primary.
  const configuredCodes = catalogue
    .map((p) => p.code)
    .filter((c) => statusByCode.get(c)?.configured)
  const available = configuredCodes.filter(
    (c) => c !== activeProviderCode && !order.includes(c),
  )
  // Drop any stale entries whose key was since removed (can't participate).
  const cleanOrder = order.filter((c) => configuredCodes.includes(c))

  function commit(next) {
    setOrder(next)
    saveMutation.mutate(next)
  }
  const move = (i, dir) => {
    const j = i + dir
    if (j < 0 || j >= cleanOrder.length) return
    const next = [...cleanOrder]
    ;[next[i], next[j]] = [next[j], next[i]]
    commit(next)
  }
  const remove = (code) => commit(cleanOrder.filter((c) => c !== code))
  const add = (code) => commit([...cleanOrder, code])

  return (
    <section className="mt-6 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
      <h3 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0 mb-1">
        Runtime fallback order
      </h3>
      <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
        If the active provider fails with a temporary error (rate limit, timeout,
        service unavailable), these providers are tried next, in order. Only
        providers with a saved key participate. Applies to report rollups and
        workspace AI template generation (SOP→tasks always uses Anthropic).
      </p>

      {/* Primary (active) — always first, not editable here. */}
      <div className="flex items-center gap-2 rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-3 py-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-accent-strong)]">
          Primary
        </span>
        <span className="text-sm font-medium text-[color:var(--color-ink-strong)]">
          {labelOf(activeProviderCode)}
        </span>
        <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
          Always tried first
        </span>
      </div>

      {/* Ordered fallback list */}
      {cleanOrder.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] px-1 py-2">
          No fallback providers yet. Add a configured provider below to enable
          automatic fallback.
        </p>
      ) : (
        <ol className="space-y-2">
          {cleanOrder.map((code, i) => (
            <li
              key={code}
              className="flex items-center gap-2 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2"
            >
              <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)] w-5">
                {i + 1}.
              </span>
              <span className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                {labelOf(code)}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0 || saveMutation.isPending}
                  aria-label="Move up"
                  className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronUp size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === cleanOrder.length - 1 || saveMutation.isPending}
                  aria-label="Move down"
                  className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronDown size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(code)}
                  disabled={saveMutation.isPending}
                  aria-label={`Remove ${labelOf(code)} from fallback`}
                  className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  <X size={15} />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {available.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[color:var(--color-ink-muted)]">Add:</span>
          {available.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => add(code)}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] hover:border-[color:var(--color-accent)] cursor-pointer"
            >
              <Plus size={12} /> {labelOf(code)}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  )
}
